import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Card, CardContent } from '@/components/ui/card';
import { Calendar, Clock, MapPin, User, Eye, Edit, Trash2, FileText } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
interface Interview {
  id: string;
  candidate_id: string;
  interview_type: string;
  scheduled_date: string;
  status: string;
  completed_at?: string;
  evaluation_score?: number;
  evaluation_notes?: string;
  recommendation?: string;
  interview_data: {
    location?: string;
    interviewer?: string;
    duration?: number;
    notes?: string;
    [key: string]: any; // For any additional fields
  };
}
interface InterviewManagerProps {
  interviews: Interview[];
  candidateName: string;
  onInterviewDeleted: (interviewId: string) => void;
  onInterviewUpdated: () => void;
}
export const InterviewManager = ({
  interviews,
  candidateName,
  onInterviewDeleted,
  onInterviewUpdated
}: InterviewManagerProps) => {
  const [selectedInterview, setSelectedInterview] = useState<Interview | null>(null);
  const [editingInterview, setEditingInterview] = useState<Interview | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [editForm, setEditForm] = useState({
    scheduledDate: '',
    scheduledTime: '',
    location: '',
    interviewer: '',
    notes: '',
    duration: '60'
  });
  const {
    toast
  } = useToast();
  const getInterviewTitle = (interview: Interview) => {
    return interview.interview_type === 'first' ? 'Πρώτη Συνέντευξη' : 'Δεύτερη Συνέντευξη';
  };
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'scheduled':
        return 'bg-blue-100 text-blue-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  const getStatusText = (status: string) => {
    switch (status) {
      case 'scheduled':
        return 'Προγραμματισμένη';
      case 'completed':
        return 'Ολοκληρωμένη';
      case 'cancelled':
        return 'Ακυρωμένη';
      default:
        return status;
    }
  };
  const handleDelete = async (interviewId: string) => {
    setIsDeleting(true);
    try {
      const {
        error
      } = await supabase.from('interviews').delete().eq('id', interviewId);
      if (error) throw error;
      onInterviewDeleted(interviewId);
      toast({
        title: "Επιτυχία",
        description: "Η συνέντευξη διαγράφηκε επιτυχώς"
      });
    } catch (error) {
      console.error('Error deleting interview:', error);
      toast({
        title: "Σφάλμα",
        description: "Δεν ήταν δυνατή η διαγραφή της συνέντευξης",
        variant: "destructive"
      });
    } finally {
      setIsDeleting(false);
    }
  };
  const handleEdit = (interview: Interview) => {
    const scheduledDate = new Date(interview.scheduled_date);
    setEditForm({
      scheduledDate: scheduledDate.toISOString().split('T')[0],
      scheduledTime: scheduledDate.toTimeString().slice(0, 5),
      location: interview.interview_data?.location || '',
      interviewer: interview.interview_data?.interviewer || '',
      notes: interview.interview_data?.notes || '',
      duration: interview.interview_data?.duration?.toString() || '60'
    });
    setEditingInterview(interview);
  };
  const handleSaveEdit = async () => {
    if (!editingInterview) return;
    try {
      const scheduledDateTime = new Date(`${editForm.scheduledDate}T${editForm.scheduledTime}`);
      const {
        error
      } = await supabase.from('interviews').update({
        scheduled_date: scheduledDateTime.toISOString(),
        interview_data: {
          location: editForm.location,
          interviewer: editForm.interviewer,
          duration: parseInt(editForm.duration),
          notes: editForm.notes
        }
      }).eq('id', editingInterview.id);
      if (error) throw error;
      setEditingInterview(null);
      onInterviewUpdated();
      toast({
        title: "Επιτυχία",
        description: "Η συνέντευξη ενημερώθηκε επιτυχώς"
      });
    } catch (error) {
      console.error('Error updating interview:', error);
      toast({
        title: "Σφάλμα",
        description: "Δεν ήταν δυνατή η ενημέρωση της συνέντευξης",
        variant: "destructive"
      });
    }
  };
  if (interviews.length === 0) {
    return <div className="text-center py-6 text-muted-foreground">
        Δεν υπάρχουν προγραμματισμένες συνεντεύξεις
      </div>;
  }
  return <div className="space-y-4">
      <h3 className="text-lg font-semibold">Συνεντεύξεις</h3>
      
      <div className="grid grid-cols-1 gap-4">
        {interviews.map(interview => <Card key={interview.id} className="overflow-hidden">
            <CardContent className="p-4">
              <div className="space-y-3">
                {/* Interview Header */}
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium">{getInterviewTitle(interview)}</h4>
                    <p className="text-sm text-muted-foreground">
                      Υποψήφιος: {candidateName}
                    </p>
                  </div>
                  <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(interview.status)}`}>
                    {getStatusText(interview.status)}
                  </span>
                </div>

                {/* Interview Details */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    <span>
                      {new Date(interview.scheduled_date).toLocaleDateString('el-GR', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    <span>
                      {new Date(interview.scheduled_date).toLocaleTimeString('el-GR', {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                    </span>
                  </div>
                  
                  {interview.interview_data?.location && <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      <span>{interview.interview_data.location}</span>
                    </div>}
                  
                  {interview.interview_data?.interviewer && <div className="flex items-center gap-2">
                      <User className="w-4 h-4" />
                      <span>{interview.interview_data.interviewer}</span>
                    </div>}
                </div>
                
                {/* Action Buttons */}
                <div className="flex space-x-2">
                  {/* Detail View */}
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button size="sm" variant="outline" className="flex-1" onClick={() => setSelectedInterview(interview)}>
                        <Eye className="h-4 w-4 mr-1" />
                        Προβολή
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
                      <DialogHeader>
                        <DialogTitle>Λεπτομέρειες Συνέντευξης</DialogTitle>
                      </DialogHeader>
                      <div className="mt-4 overflow-y-auto flex-1 pr-2">
                        {selectedInterview && <div className="space-y-6">
                            {/* Basic Information */}
                            <div>
                              <h4 className="font-medium text-base mb-3">Βασικές Πληροφορίες</h4>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                  <label className="text-sm font-medium">Τύπος Συνέντευξης</label>
                                  <p className="text-muted-foreground">{getInterviewTitle(selectedInterview)}</p>
                                </div>
                                <div>
                                  <label className="text-sm font-medium">Κατάσταση</label>
                                  <p className="text-muted-foreground">{getStatusText(selectedInterview.status)}</p>
                                </div>
                                <div>
                                  <label className="text-sm font-medium">Ημερομηνία Προγραμματισμού</label>
                                  <p className="text-muted-foreground">
                                    {new Date(selectedInterview.scheduled_date).toLocaleDateString('el-GR', {
                                weekday: 'long',
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              })}
                                  </p>
                                </div>
                                <div>
                                  <label className="text-sm font-medium">Ώρα Προγραμματισμού</label>
                                  <p className="text-muted-foreground">
                                    {new Date(selectedInterview.scheduled_date).toLocaleTimeString('el-GR', {
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                                  </p>
                                </div>
                                {selectedInterview.completed_at && <>
                                    <div>
                                      <label className="text-sm font-medium">Ημερομηνία Ολοκλήρωσης</label>
                                      <p className="text-muted-foreground">
                                        {new Date(selectedInterview.completed_at).toLocaleDateString('el-GR', {
                                  weekday: 'long',
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric'
                                })}
                                      </p>
                                    </div>
                                    <div>
                                      <label className="text-sm font-medium">Ώρα Ολοκλήρωσης</label>
                                      <p className="text-muted-foreground">
                                        {new Date(selectedInterview.completed_at).toLocaleTimeString('el-GR', {
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                                      </p>
                                    </div>
                                  </>}
                              </div>
                            </div>

                            {/* Interview Details */}
                            <div>
                              <h4 className="font-medium text-base mb-3">Λεπτομέρειες Συνέντευξης</h4>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {selectedInterview.interview_data?.location && <div>
                                    <label className="text-sm font-medium">Τοποθεσία</label>
                                    <p className="text-muted-foreground">{selectedInterview.interview_data.location}</p>
                                  </div>}
                                {selectedInterview.interview_data?.interviewer && <div>
                                    <label className="text-sm font-medium">Συνεντευκτής</label>
                                    <p className="text-muted-foreground">{selectedInterview.interview_data.interviewer}</p>
                                  </div>}
                                {selectedInterview.interview_data?.duration && <div>
                                    <label className="text-sm font-medium">Διάρκεια</label>
                                    <p className="text-muted-foreground">{selectedInterview.interview_data.duration} λεπτά</p>
                                  </div>}
                              </div>
                            </div>

                            {/* Evaluation Results */}
                            {(selectedInterview.evaluation_score || selectedInterview.evaluation_notes || selectedInterview.recommendation) && <div>
                                <h4 className="font-medium text-base mb-3">Αποτελέσματα Αξιολόγησης</h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  {selectedInterview.evaluation_score && <div>
                                      <label className="text-sm font-medium">Βαθμολογία</label>
                                      <p className="text-muted-foreground">{selectedInterview.evaluation_score}/10</p>
                                    </div>}
                                  {selectedInterview.recommendation && <div>
                                      <label className="text-sm font-medium">Σύσταση</label>
                                      <p className="text-muted-foreground">
                                        {selectedInterview.recommendation === 'proceed' ? 'Προχώρηση' : selectedInterview.recommendation === 'reject' ? 'Απόρριψη' : selectedInterview.recommendation === 'pending' ? 'Εκκρεμεί' : selectedInterview.recommendation}
                                      </p>
                                    </div>}
                                </div>
                                {selectedInterview.evaluation_notes && <div className="mt-4">
                                    <label className="text-sm font-medium">Σημειώσεις Αξιολόγησης</label>
                                    <p className="text-muted-foreground whitespace-pre-wrap">{selectedInterview.evaluation_notes}</p>
                                  </div>}
                              </div>}

                            {/* Additional Interview Data */}
                            {selectedInterview.interview_data && Object.keys(selectedInterview.interview_data).length > 0 && <div>
                                <h4 className="font-medium text-base mb-3">Πρόσθετες Πληροφορίες</h4>
                                <div className="space-y-3">
                                {Object.entries(selectedInterview.interview_data).map(([key, value]) => {
                            // Skip already displayed fields
                            if (['location', 'interviewer', 'duration', 'notes'].includes(key) || !value) {
                              return null;
                            }

                            // Helper function to check if URL is an image
                            const isImageUrl = (url: string) => {
                              try {
                                // Check for common image extensions in the URL
                                const imageExtensions = /\.(jpg|jpeg|png|gif|webp|bmp|svg)/i;
                                return imageExtensions.test(url) || url.includes('supabase.co/storage') || url.includes('candidate-documents');
                              } catch {
                                return false;
                              }
                            };

                            // Helper function to render URLs as images if they're image URLs
                            const renderValue = (val: any) => {
                              // Handle arrays (like document URLs arrays)
                              if (Array.isArray(val)) {
                                return <div className="space-y-3">
                                            {val.map((item, index) => <div key={index}>
                                                {renderValue(item)}
                                              </div>)}
                                          </div>;
                              }

                              // Handle single URL strings
                              if (typeof val === 'string' && val.startsWith('http')) {
                                // Always treat URLs from our storage as images for document URLs
                                if (val.includes('supabase.co/storage') || val.includes('candidate-documents') || isImageUrl(val)) {
                                  return <div className="mt-2 space-y-2">
                                              <div className="relative inline-block">
                                                <img src={val} alt="Document" className="max-w-sm max-h-48 object-contain rounded border cursor-pointer hover:opacity-80" onClick={() => window.open(val, '_blank')} onError={e => {
                                        // If image fails to load, show a document icon instead
                                        e.currentTarget.style.display = 'none';
                                        const parent = e.currentTarget.parentElement;
                                        if (parent && !parent.querySelector('.document-fallback')) {
                                          const fallback = document.createElement('div');
                                          fallback.className = 'document-fallback flex items-center justify-center w-32 h-32 bg-muted rounded border';
                                          fallback.innerHTML = '<svg class="w-8 h-8 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path></svg>';
                                          parent.insertBefore(fallback, e.currentTarget);
                                        }
                                      }} />
                                              </div>
                                              <div className="flex items-center gap-2">
                                                <Button size="sm" variant="outline" onClick={() => {
                                        const link = document.createElement('a');
                                        link.href = val;
                                        link.download = val.split('/').pop()?.split('?')[0] || 'document';
                                        link.target = '_blank';
                                        document.body.appendChild(link);
                                        link.click();
                                        document.body.removeChild(link);
                                      }}>
                                                  <FileText className="h-4 w-4 mr-1" />
                                                  Κατέβασμα
                                                </Button>
                                                <Button size="sm" variant="ghost" onClick={() => window.open(val, '_blank')}>
                                                  <Eye className="h-4 w-4 mr-1" />
                                                  Πλήρης Προβολή
                                                </Button>
                                              </div>
                                              <p className="text-xs text-muted-foreground break-all">{val}</p>
                                            </div>;
                                } else {
                                  // Non-image URL, show as link
                                  return <div className="mt-2">
                                              <a href={val} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline break-all">
                                                {val}
                                              </a>
                                            </div>;
                                }
                              } else if (typeof val === 'object') {
                                return <pre className="text-xs bg-muted p-2 rounded">{JSON.stringify(val, null, 2)}</pre>;
                              } else {
                                return <span>{String(val)}</span>;
                              }
                            };
                            // Helper function to get proper field display names
                            const getFieldDisplayName = (fieldKey: string) => {
                              if (fieldKey === 'ankaNumber') return 'AMKA Number';
                              if (fieldKey === 'amaNumber') return 'AMA Number';
                              return fieldKey.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
                            };

                            return <div key={key}>
                                        <label className="text-sm font-medium capitalize">
                                          {getFieldDisplayName(key)}
                                        </label>
                                        <div className="text-muted-foreground">
                                          {renderValue(value)}
                                        </div>
                                      </div>;
                          })}
                                </div>
                              </div>}

                            {/* Notes */}
                            {selectedInterview.interview_data?.notes && <div>
                                <label className="text-sm font-medium">Σημειώσεις</label>
                                <p className="text-muted-foreground whitespace-pre-wrap">{selectedInterview.interview_data.notes}</p>
                              </div>}
                          </div>}
                      </div>
                    </DialogContent>
                  </Dialog>
                  
                  {/* Edit */}
                  <Dialog open={editingInterview?.id === interview.id} onOpenChange={open => !open && setEditingInterview(null)}>
                    <DialogTrigger asChild>
                      
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl">
                      <DialogHeader>
                        <DialogTitle>Επεξεργασία Συνέντευξης</DialogTitle>
                      </DialogHeader>
                      <div className="mt-4 space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="edit-date">Ημερομηνία</Label>
                            <Input id="edit-date" type="date" value={editForm.scheduledDate} onChange={e => setEditForm({
                          ...editForm,
                          scheduledDate: e.target.value
                        })} />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="edit-time">Ώρα</Label>
                            <Input id="edit-time" type="time" value={editForm.scheduledTime} onChange={e => setEditForm({
                          ...editForm,
                          scheduledTime: e.target.value
                        })} />
                          </div>
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="edit-location">Τοποθεσία</Label>
                          <Input id="edit-location" value={editForm.location} onChange={e => setEditForm({
                        ...editForm,
                        location: e.target.value
                      })} />
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="edit-interviewer">Συνεντευκτής</Label>
                            <Input id="edit-interviewer" value={editForm.interviewer} onChange={e => setEditForm({
                          ...editForm,
                          interviewer: e.target.value
                        })} />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="edit-duration">Διάρκεια (λεπτά)</Label>
                            <Input id="edit-duration" type="number" value={editForm.duration} onChange={e => setEditForm({
                          ...editForm,
                          duration: e.target.value
                        })} />
                          </div>
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="edit-notes">Σημειώσεις</Label>
                          <Textarea id="edit-notes" value={editForm.notes} onChange={e => setEditForm({
                        ...editForm,
                        notes: e.target.value
                      })} rows={3} />
                        </div>
                        
                        <div className="flex space-x-2">
                          <Button onClick={handleSaveEdit} className="flex-1">
                            Αποθήκευση
                          </Button>
                          <Button variant="outline" onClick={() => setEditingInterview(null)}>
                            Ακύρωση
                          </Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                  
                  {/* Delete */}
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button size="sm" variant="outline" className="text-destructive hover:text-destructive">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Διαγραφή Συνέντευξης</AlertDialogTitle>
                        <AlertDialogDescription>
                          Είστε βέβαιοι ότι θέλετε να διαγράψετε αυτή τη συνέντευξη; 
                          Αυτή η ενέργεια δεν μπορεί να αναιρεθεί.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Ακύρωση</AlertDialogCancel>
                        <AlertDialogAction onClick={() => handleDelete(interview.id)} disabled={isDeleting} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                          {isDeleting ? "Διαγραφή..." : "Διαγραφή"}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            </CardContent>
          </Card>)}
      </div>
    </div>;
};